import { useChatbot } from '@/feature/chatbot/useChatbot';
import { useChatbotHistory } from '@/feature/chatbot/useChatbotHistory';
import { useTexts, useTranslation } from '@/feature/lang';
import '@/icon/MaterialSymbol/font';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MIN_CHAT_HEIGHT, MIN_CHAT_WIDTH } from './styles';

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  '& .MuiIconButton-root': {
    color: theme.palette.text.secondary,
  },
  '& .material-symbols-rounded': {
    fontSize: 20,
    fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
  },
}));

const ContextIndicator = styled('div', {
  shouldForwardProp: (prop) => prop !== 'show',
})<{ show: boolean }>(({ theme, show }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  marginLeft: theme.spacing(1),
  opacity: show ? 1 : 0,
  transition: theme.transitions.create('opacity'),
}));

const ResizeIconButton = styled(IconButton)(({ theme }) => ({
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
}));

interface ChatWindowHeaderProps {
  onClose: () => void;
  isHistoryOpen: boolean;
  onToggleHistory: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  onClearChat?: () => void;
  onResize?: (width: number, height: number) => void;
  chatWindowRef?: React.Ref<HTMLElement>;
  initialWidth?: number;
  initialHeight?: number;
}

export const ChatWindowHeader = ({
  onClose,
  isHistoryOpen,
  onToggleHistory,
  onToggleFullscreen,
  isFullscreen,
  onClearChat,
  onResize,
  chatWindowRef,
  initialWidth,
  initialHeight,
}: ChatWindowHeaderProps) => {
  const texts = useTexts().component.chatbot.header;
  const { messages } = useChatbot();
  const { translate } = useTranslation();
  const [showIndicator, setShowIndicator] = useState(false);
  const isResizingRef = useRef(false);
  const startPositionRef = useRef({ x: 0, y: 0 });
  const startDimensionsRef = useRef({ width: 0, height: 0 });
  const { hasHistory } = useChatbotHistory();

  // Flash the indicator when messages changes
  useEffect(() => {
    setShowIndicator(true);
    const timer = setTimeout(() => setShowIndicator(false), 1000);
    return () => clearTimeout(timer);
  }, [messages]);

  const handleResize = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current || !chatWindowRef || !onResize) return;

      const element = 'current' in chatWindowRef ? chatWindowRef.current : null;
      if (!element) return;

      const deltaX = e.clientX - startPositionRef.current.x;
      const deltaY = e.clientY - startPositionRef.current.y;

      const newWidth = Math.max(
        MIN_CHAT_WIDTH,
        startDimensionsRef.current.width - deltaX
      );
      const newHeight = Math.max(
        MIN_CHAT_HEIGHT,
        startDimensionsRef.current.height - deltaY
      );

      onResize(newWidth, newHeight);
    },
    [chatWindowRef, onResize]
  );

  const handleResizeEnd = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current) {
        return;
      }
      isResizingRef.current = false;
      window.removeEventListener('mousemove', handleResize, { capture: true });
      window.removeEventListener('mouseup', handleResizeEnd, { capture: true });
      document.body.style.cursor = '';
      e.preventDefault();
      e.stopPropagation();
    },
    [handleResize]
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (!chatWindowRef || !onResize) {
        return;
      }

      const element = 'current' in chatWindowRef ? chatWindowRef.current : null;
      if (!element) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      isResizingRef.current = true;
      document.body.style.cursor = 'grabbing';

      startPositionRef.current = {
        x: e.clientX,
        y: e.clientY,
      };

      const rect = element.getBoundingClientRect();
      startDimensionsRef.current = {
        width: initialWidth || rect.width,
        height: initialHeight || rect.height,
      };

      window.addEventListener('mousemove', handleResize, { capture: true });
      window.addEventListener('mouseup', handleResizeEnd, { capture: true });
    },
    [
      chatWindowRef,
      onResize,
      initialWidth,
      initialHeight,
      handleResize,
      handleResizeEnd,
    ]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleResize, { capture: true });
      window.removeEventListener('mouseup', handleResizeEnd, { capture: true });
      document.body.style.cursor = '';
    };
  }, [handleResize, handleResizeEnd]);

  return (
    <HeaderContainer>
      <Box
        component="div"
        display="flex"
        alignItems="center"
        color="text.secondary"
      >
        {onResize && chatWindowRef && !isFullscreen && (
          <ResizeIconButton
            size="small"
            onMouseDown={handleResizeStart}
            aria-label="resize chat window"
          >
            <span className="material-symbols-rounded">drag_indicator</span>
          </ResizeIconButton>
        )}

        {hasHistory && !isHistoryOpen && (
          <IconButton onClick={onToggleHistory} size="small">
            <span className="material-symbols-rounded">history</span>
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={onClearChat}
          title={translate(texts.clearChatButton.tooltip)}
        >
          <span className="material-symbols-rounded">delete</span>
        </IconButton>
        <ContextIndicator show={showIndicator} />
      </Box>
      <Box component="div" display="flex" alignItems="center">
        {onToggleFullscreen && (
          <IconButton onClick={onToggleFullscreen} size="small">
            {isFullscreen ? (
              <span className="material-symbols-rounded">close_fullscreen</span>
            ) : (
              <span className="material-symbols-rounded">open_in_full</span>
            )}
          </IconButton>
        )}
        <IconButton onClick={onClose} size="small">
          <span className="material-symbols-rounded">close</span>
        </IconButton>
      </Box>
    </HeaderContainer>
  );
};
